const LOCAL = /^(.*\.vm\.cinch\.io|localhost|[0-9.]+)(:[0-9]+)?$/gim;
const DEV = ['beta.marketing-dev.cinch.io', 'marketing-dev.cinch.io'];
const PROD = ['beta.marketing.cinch.io', 'marketing.cinch.io'];

/**
 * Constants used throughout the application.
 * @type {Object} - The CONSTANTS object to help reuse some values throughout the application and maintain consistency.
 *
 * @property {Object} URL - The URL constants; ADD_TEMPLATE_MAIL, LIST_TEMPLATE
 * @property {Object} MODES - The modes constants; TEMPLATE: CREATE, PREVIEW, PUBLISH. Used in the template editor.
 * @property {string[]} READ_ONLY_CUSTOMER_FIELDS - The array of fields that are read-only in the customer editor.
 * @property {string[]} PROVINCES_CANADA - The array of Canadian provinces. eh!
 * @property {Object[]} TIMEZONES - The array of timezones. Used in the timezone dropdown.
 * @property {string} ENV - The environment the application is running in. LOCAL, DEV, PROD.
 */

const CONSTANTS = {
  URL: {
    ADD_TEMPLATE_MAIL: '/templates/print/add',
    LIST_TEMPLATE: '/templates'
  },
  MODES: {
    TEMPLATE: {
      CREATE: 'CREATE',
      PREVIEW: 'PREVIEW',
      PUBLISHV: 'PUBLISH'
    }
  },
  READ_ONLY_CUSTOMER_FIELDS: ['id', 'company_id', 'created_when', 'updated_when', 'deleted_when', 'entity_ref', 'entity_source', 'entity_number', 'index_rank', 'customer_value', 'customer_value_segment', 'visit_trend_days', 'predicted_return_date', 'predicted_visit_next_12_months', 'predicted_spend_next_12_months', 'product_preferences', 'recommended_products', 'pricing_sensitivity', 'profile_attributes__customer_value_segment', 'last_transaction_when', 'first_transaction_when', 'appended_data__', 'stat_last_location_id', 'stat_last_location__name', 'stat_last_location__address1', 'stat_last_location__city', 'stat_last_location__state', 'stat_last_location__zip_code', 'stat_last_location__phone', 'stat_last_location__entity_ref'],
  /**
   * Canadian provinces and territories
   * @type {Object[]} The object format is: {id, label}
   */
  CANADIAN_PROVINCES: [{
    id: 'AB',
    label: 'Alberta'
  }, {
    id: 'BC',
    label: 'British Columbia'
  }, {
    id: 'MB',
    label: 'Manitoba'
  }, {
    id: 'NB',
    label: 'New Brunswick'
  }, {
    id: 'NL',
    label: 'Newfoundland and Labrador'
  }, {
    id: 'NS',
    label: 'Nova Scotia'
  }, {
    id: 'NT',
    label: 'Northwest Territories'
  }, {
    id: 'NU',
    label: 'Nunavut'
  }, {
    id: 'ON',
    label: 'Ontario'
  }, {
    id: 'PE',
    label: 'Prince Edward Island'
  }, {
    id: 'QC',
    label: 'Quebec'
  }, {
    id: 'SK',
    label: 'Saskatchewan'
  }, {
    id: 'YT',
    label: 'Yukon'
  }],
  /**
   * US states and territories
   * @type {Object[]} The object format is: {id, label}
   */
  US_STATES: [{
    id: 'AL',
    label: 'Alabama'
  }, {
    id: 'AK',
    label: 'Alaska'
  }, {
    id: 'AZ',
    label: 'Arizona'
  }, {
    id: 'AR',
    label: 'Arkansas'
  }, {
    id: 'CA',
    label: 'California'
  }, {
    id: 'CO',
    label: 'Colorado'
  }, {
    id: 'CT',
    label: 'Connecticut'
  }, {
    id: 'DE',
    label: 'Delaware'
  }, {
    id: 'FL',
    label: 'Florida'
  }, {
    id: 'GA',
    label: 'Georgia'
  }, {
    id: 'HI',
    label: 'Hawaii'
  }, {
    id: 'ID',
    label: 'Idaho'
  }, {
    id: 'IL',
    label: 'Illinois'
  }, {
    id: 'IN',
    label: 'Indiana'
  }, {
    id: 'IA',
    label: 'Iowa'
  }, {
    id: 'KS',
    label: 'Kansas'
  }, {
    id: 'KY',
    label: 'Kentucky'
  }, {
    id: 'LA',
    label: 'Louisiana'
  }, {
    id: 'ME',
    label: 'Maine'
  }, {
    id: 'MD',
    label: 'Maryland'
  }, {
    id: 'MA',
    label: 'Massachusetts'
  }, {
    id: 'MI',
    label: 'Michigan'
  }, {
    id: 'MN',
    label: 'Minnesota'
  }, {
    id: 'MS',
    label: 'Mississippi'
  }, {
    id: 'MO',
    label: 'Missouri'
  }, {
    id: 'MT',
    label: 'Montana'
  }, {
    id: 'NE',
    label: 'Nebraska'
  }, {
    id: 'NV',
    label: 'Nevada'
  }, {
    id: 'NH',
    label: 'New Hampshire'
  }, {
    id: 'NJ',
    label: 'New Jersey'
  }, {
    id: 'NM',
    label: 'New Mexico'
  }, {
    id: 'NY',
    label: 'New York'
  }, {
    id: 'NC',
    label: 'North Carolina'
  }, {
    id: 'ND',
    label: 'North Dakota'
  }, {
    id: 'OH',
    label: 'Ohio'
  }, {
    id: 'OK',
    label: 'Oklahoma'
  }, {
    id: 'OR',
    label: 'Oregon'
  }, {
    id: 'PA',
    label: 'Pennsylvania'
  }, {
    id: 'RI',
    label: 'Rhode Island'
  }, {
    id: 'SC',
    label: 'South Carolina'
  }, {
    id: 'SD',
    label: 'South Dakota'
  }, {
    id: 'TN',
    label: 'Tennessee'
  }, {
    id: 'TX',
    label: 'Texas'
  }, {
    id: 'UT',
    label: 'Utah'
  }, {
    id: 'VT',
    label: 'Vermont'
  }, {
    id: 'VA',
    label: 'Virginia'
  }, {
    id: 'WA',
    label: 'Washington'
  }, {
    id: 'WV',
    label: 'West Virginia'
  }, {
    id: 'WI',
    label: 'Wisconsin'
  }, {
    id: 'WY',
    label: 'Wyoming'
  }, {
    id: 'DC',
    label: 'District of Columbia'
  }, {
    id: 'PR',
    label: 'Puerto Rico'
  }, {
    id: 'VI',
    label: 'Virgin Islands'
  }, {
    id: 'GU',
    label: 'Guam'
  }, {
    id: 'AS',
    label: 'American Samoa'
  }, {
    id: 'MP',
    label: 'Northern Mariana Islands'
  }],
  /**
   * Countries
   * @type {Object[]} The object format is: {id, label}
   */
  COUNTRIES: [{
    id: 'US',
    label: 'United States'
  }, {
    id: 'CA',
    label: 'Canada'
  }, {
    id: 'MX',
    label: 'Mexico'
  }, {
    id: 'GB',
    label: 'United Kingdom'
  }, {
    id: 'AU',
    label: 'Australia'
  }, {
    id: 'DE',
    label: 'Germany'
  }, {
    id: 'FR',
    label: 'France'
  }, {
    id: 'IT',
    label: 'Italy'
  }, {
    id: 'ES',
    label: 'Spain'
  }, {
    id: 'JP',
    label: 'Japan'
  }, {
    id: 'CN',
    label: 'China'
  }, {
    id: 'IN',
    label: 'India'
  }, {
    id: 'BR',
    label: 'Brazil'
  }, {
    id: 'AR',
    label: 'Argentina'
  }, {
    id: 'NL',
    label: 'Netherlands'
  }, {
    id: 'BE',
    label: 'Belgium'
  }, {
    id: 'CH',
    label: 'Switzerland'
  }, {
    id: 'AT',
    label: 'Austria'
  }, {
    id: 'SE',
    label: 'Sweden'
  }, {
    id: 'NO',
    label: 'Norway'
  }, {
    id: 'DK',
    label: 'Denmark'
  }, {
    id: 'FI',
    label: 'Finland'
  }, {
    id: 'PL',
    label: 'Poland'
  }, {
    id: 'CZ',
    label: 'Czech Republic'
  }, {
    id: 'HU',
    label: 'Hungary'
  }, {
    id: 'RO',
    label: 'Romania'
  }, {
    id: 'BG',
    label: 'Bulgaria'
  }, {
    id: 'HR',
    label: 'Croatia'
  }, {
    id: 'GR',
    label: 'Greece'
  }, {
    id: 'TR',
    label: 'Turkey'
  }, {
    id: 'RU',
    label: 'Russia'
  }, {
    id: 'UA',
    label: 'Ukraine'
  }, {
    id: 'BY',
    label: 'Belarus'
  }, {
    id: 'LT',
    label: 'Lithuania'
  }, {
    id: 'LV',
    label: 'Latvia'
  }, {
    id: 'EE',
    label: 'Estonia'
  }, {
    id: 'IE',
    label: 'Ireland'
  }, {
    id: 'PT',
    label: 'Portugal'
  }, {
    id: 'ZA',
    label: 'South Africa'
  }, {
    id: 'EG',
    label: 'Egypt'
  }, {
    id: 'MA',
    label: 'Morocco'
  }, {
    id: 'NG',
    label: 'Nigeria'
  }, {
    id: 'KE',
    label: 'Kenya'
  }, {
    id: 'GH',
    label: 'Ghana'
  }, {
    id: 'KR',
    label: 'South Korea'
  }, {
    id: 'TH',
    label: 'Thailand'
  }, {
    id: 'VN',
    label: 'Vietnam'
  }, {
    id: 'PH',
    label: 'Philippines'
  }, {
    id: 'ID',
    label: 'Indonesia'
  }, {
    id: 'MY',
    label: 'Malaysia'
  }, {
    id: 'SG',
    label: 'Singapore'
  }, {
    id: 'NZ',
    label: 'New Zealand'
  }],
  /**
   * A list of timezones
   * @type {Object[]} The object format is: {id, label}
   */
  TIMEZONES: [{
    id: 'America/New_York',
    label: 'Eastern Standard Time',
    abbr: 'EST'
  }, {
    id: 'America/Chicago',
    label: 'Central Standard Time',
    abbr: 'CST'
  }, {
    id: 'America/Denver',
    label: 'Mountain Standard Time',
    abbr: 'MST'
  }, {
    id: 'America/Los_Angeles',
    label: 'Pacific Standard Time',
    abbr: 'PST'
  }, {
    id: 'America/Anchorage',
    label: 'Alaska Standard Time',
    abbr: 'AKST'
  }, {
    id: 'Pacific/Honolulu',
    label: 'Hawaii Standard Time',
    abbr: 'HST'
  }, {
    id: 'Etc/GMT+4',
    label: 'Atlantic Standard Time',
    abbr: 'AST'
  }, {
    id: 'Etc/GMT+7',
    label: 'Arizona',
    abbr: 'AZ'
  }],
  VOICE_AGENT_SESSION_FIELDS: [{
    id: 'coupon_id',
    label: 'Coupon ID'
  }, {
    id: 'promo_code',
    label: 'Promo Code'
  }, {
    id: 'coupon_name',
    label: 'Coupon Name'
  }, {
    id: 'discount_type',
    label: 'Discount Type'
  }, {
    id: 'discount_amount',
    label: 'Discount Amount'
  }, {
    id: 'description',
    label: 'Description'
  }, {
    id: 'end_when',
    label: 'End When'
  }, {
    id: 'coupon_requested',
    label: 'Coupon Requested'
  }, {
    id: 'callback_requested',
    label: 'Callback Requested'
  }, {
    id: 'callback_preferred_phone',
    label: 'Callback Preferred Phone'
  }, {
    id: 'callback_time_start',
    label: 'Callback Time Start'
  }, {
    id: 'callback_time_end',
    label: 'Callback Time End'
  }, {
    id: 'callback_caller_message',
    label: 'Callback Caller Message'
  }, {
    id: 'address_requested',
    label: 'Address Requested'
  }],
  ENV: window.location.host.match(LOCAL) ? 'LOCAL' : DEV.includes(window.location.host) ? 'DEV' : 'PROD'
};
export default CONSTANTS;